import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { selectServiceProviderAvailabilitys, selectServiceProviderAvailabilitysLoading } from '../../stateManagement/serviceProviderAvailabilitySlice';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { selectSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';
import { AppDispatch } from '../../stateManagement/store';
import { IAppointment, ICustomAppointment } from "@ontime/types";
import GridTable, { defaultPaginationData, GridColumnType, PaginationType } from '../../gridTable/GridTable';
import AppointmentService from '../../services/AppointmentService';
import AppointmentDialog from './AppointmentDialog';
import DateSelectable from '../DateSelectable';
import AppointmentDetailDialog from './AppointmentDetailDialog';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import BlockIcon from '@mui/icons-material/Block';
import StarIcon from '@mui/icons-material/Star';
import ServiceProviderMenu from '../menu/ServiceProviderMenu';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import InfoComponent from '../../customComponents/InfoComponent';
import { selectInfo } from '../../stateManagement/infoSlice';
import { fetchOrganizationTasks } from '../../stateManagement/organizationTaskSlice';
import ClientName from '../client/ClientName';
import { AppointmentStatus } from "@ontime/types";

export const getFullName = (match: IAppointment) => {
    return match.clientId ?
        <ClientName clientId={match.clientId} showdetail={false} /> :
        match.userId ?
            `${match.user?.fullName} ` :
            `${match.guestFirstName}${match.guestLastName ? ` ${match.guestLastName} ` : ' '}`;
};

const renderCustomStatus = (input: AppointmentStatus) => {
    switch (input) {
        case AppointmentStatus.Canceled:
            return <Box color='red' ><BlockIcon /></Box>;
        case AppointmentStatus.Confirmed:
            return <Box color='green'><DoneIcon /></Box>;
        case AppointmentStatus.Completed:
            return <Box color='green'><DoneAllIcon /></Box>;
        default:
            return (
                <Box
                    display='flex'
                    sx={{
                        animation: 'blinker 1.5s linear infinite',
                        '@keyframes blinker': {
                            '50%': { opacity: 0 }
                        }
                    }}
                >
                    <StarIcon />
                    <Typography color="error">New</Typography>
                    <StarIcon />
                </Box>
            );
    }
};

export const AppointmentToCustom = (input: IAppointment): ICustomAppointment => {
    const start = dayjs(input.startDateTime);
    const end = input.endDateTime ? dayjs(input.endDateTime) : start;
    const duration = end.diff(start, 'minute');

    return {
        ...input,
        customClientName: getFullName(input),
        customStartTime: start.format('hh:mm A'),
        customDuration: `${duration} min`,
        customStatus: (
            <Box display='flex' alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {AppointmentStatus[input.status]}
                </Typography>
                {renderCustomStatus(input.status)}
            </Box>
        ),
    };
};

const AppointmentList: React.FC = () => {
    const [loading, setLoading] = useState(false);

    const infoState = useSelector(selectInfo);

    const selectedOrganization = useSelector(selectSelectedOrganization);
    const selectedServiceProvider = useSelector(selectSelectedServiceProvider);

    const [selectedDate, setSelectedDate] = useState(dayjs());

    const [listType, setListType] = useState<'timeSheet' | 'gridTable'>('timeSheet');

    const dispatch = useDispatch<AppDispatch>();

    const serviceProviderAvailabilities = useSelector(selectServiceProviderAvailabilitys);
    const serviceProviderAvailabilitiesLoading = useSelector(selectServiceProviderAvailabilitysLoading);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const timeSlotRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const [appointmentDialogState, setAppointmentDialogState] = useState<{
        open: boolean;
        startDateTime?: string;
        existData?: IAppointment;
    }>({ open: false });

    const closeAppointmentDialog = (input?: IAppointment) => {
        setAppointmentDialogState({ open: false });
        if (input) {
            if (appointments.find((item) => item.appointmentId === input.appointmentId)) {
                setAppointments([...appointments.filter((item) => item.appointmentId !== input.appointmentId), AppointmentToCustom(input)]);
            }
            else {
                setAppointments([...appointments, AppointmentToCustom(input)]);
            }
        }
    };

    const openAppointmentDialog = (hour: number, minute: number, input?: IAppointment) => {
        setAppointmentDialogState({
            open: true,
            startDateTime: selectedDate
                .hour(hour)
                .minute(minute)
                .second(0)
                .millisecond(0)
                .format('YYYY-MM-DDTHH:mm:ss.SSS'),
            existData: input,
        });
    };

    const handleOpenEmptyAppointmentDialog = () => {
        openAppointmentDialog(0, 0);
    };

    const [appointmentDetailDialogState, setAppointmentDetailDialogState] = useState<{
        open: boolean;
        detail?: IAppointment;
        fullName?: string
    }>({ open: false });

    const closeAppointmentDetailDialog = (input?: IAppointment) => {
        setAppointmentDetailDialogState({ open: false });
        // if (input) {
        //     if (appointments.find((item) => item.id === input.id)) {
        //         setAppointments([...appointments.filter((item) => item.id !== input.id), input]);
        //     }
        //     else {
        //         setAppointments([...appointments, input]);
        //     }
        // }

    };

    const openAppointmentDetailDialog = (input: IAppointment) => {
        setAppointmentDetailDialogState({
            open: true,
            detail: input,
        });
    };

    const [appointments, setAppointments] = useState<ICustomAppointment[]>([]);

    const appointmentType: GridColumnType<ICustomAppointment> = {
        appointmentId: { type: "Id", dontShow: true },
        serviceProviderId: { type: "Id", dontShow: true },
        organizationId: { type: "Id", dontShow: true },
        guestFirstName: { type: "Text", header: 'guestFirstName', dontShow: true },
        customStartTime: { type: "Text", header: 'Start Time' },
        customClientName: { type: "Element", header: 'Client' },
        customDuration: { type: "Text", header: 'Duration' },
        customStatus: { type: "Element", header: 'Status' },
        startDateTime: { type: "DateTime", header: 'startDateTime', dontShow: true },
        status: { type: "Enum", header: 'Status', enumObject: AppointmentStatus, dontShow: true },
        version: { type: "Id", dontShow: true },
    };

    const [paginationData, setPaginationData] = useState<PaginationType>({ ...defaultPaginationData, maxResultCount: 10000 });

    const getAll = async () => {
        setLoading(true);
        setAppointments([]);
        try {
            const response = await AppointmentService.GetList(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId,
                //     {
                //     organizationId: selectedOrganization.organizationId ,
                //     serviceProviderId: selectedServiceProvider.serviceProviderId ,
                //     date: selectedDate.format('YYYY-MM-DDTHH:mm:ss.SSS')
                // }, paginationData
                selectedDate.format('YYYY-MM-DD')
            );
            setAppointments(response.map((item) => AppointmentToCustom(item)));
            // setPaginationData({ ...paginationData, totalCount: response.data.result.totalCount });
            setPaginationData({ ...paginationData, totalCount: response.length });
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedDate && selectedOrganization.organizationId && selectedServiceProvider.serviceProviderId) {
            getAll();
        }
    }, [selectedDate, selectedServiceProvider.serviceProviderId]);

    // useEffect(() => {
    //     if (selectedOrganization.organizationId && selectedServiceProvider.serviceProviderId) {

    //         dispatch(fetchServiceProviderAvailabilitys({
    //             organizationId: selectedOrganization.organizationId,
    //             serviceProviderId: selectedServiceProvider.serviceProviderId
    //         }));
    //     }
    // }, [selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId]);

    useEffect(() => {
        if (selectedOrganization.organizationId) {
            dispatch(fetchOrganizationTasks({ organizationId: selectedOrganization.organizationId, showDeleted: false }))
        }
    }, [selectedOrganization.organizationId]);

    const checkHour = (hour: number, min: number): boolean => {
        const selectedDayStart = selectedDate.startOf('day');

        return serviceProviderAvailabilities.some((sp) =>
            (dayjs(sp.startDate).startOf('day').isBefore(selectedDayStart)
                || dayjs(sp.startDate).startOf('day').isSame(selectedDayStart)
            ) &&
            (!sp.endDate || dayjs(sp.endDate).startOf('day').isAfter(selectedDayStart)) &&
            sp.serviceProviderAvailabilityDays.some((day) => {
                const availabilityStart = dayjs(day.availabilityStart, 'HH:mm:ss');
                const availabilityEnd = dayjs(day.availabilityEnd, 'HH:mm:ss');
                const checkTime = dayjs().hour(hour).minute(min).second(0);
                return selectedDate.day() === day.dayOfWeek &&
                    availabilityStart.isBefore(checkTime) &&
                    checkTime.isBefore(availabilityEnd);
            })
        );
    };

    const getAppointment = (hour: number, min: number) => {
        const matchStartDateTime = appointments.filter((item) => item.status !== AppointmentStatus.Canceled).find((appointment) => {
            const appointmentTime = dayjs(appointment.startDateTime);
            return (
                appointmentTime.isSame(selectedDate, 'day') &&
                appointmentTime.hour() === hour &&
                appointmentTime.minute() === min
            );
        });

        const match = appointments.filter((item) => item.status !== AppointmentStatus.Canceled).find((appointment) => {
            const startDateTime = dayjs(appointment.startDateTime);
            const endDateTime = appointment.endDateTime
                ? dayjs(appointment.endDateTime)
                : startDateTime.add(15, 'minute');

            const slotTime = dayjs(selectedDate).hour(hour).minute(min);

            return (
                startDateTime.isSame(selectedDate, 'day') &&
                (slotTime.isAfter(startDateTime) || slotTime.isSame(startDateTime)) &&
                slotTime.isBefore(endDateTime)
            );
        });

        if (!match) {
            return (
                <InfoComponent infoContent='Add new appointment by client or guest in this clock'>
                    <IconButton size='small' disabled={loading} onClick={() => openAppointmentDialog(hour, min)} sx={{ marginLeft: '7px' }}>
                        <AddIcon />
                    </IconButton>
                </InfoComponent>
            );
        }

        const isStartSlot = matchStartDateTime?.appointmentId === match.appointmentId;

        const startDateTime = dayjs(match.startDateTime);

        const durationMins = (match.endDateTime ? dayjs(match.endDateTime) : startDateTime.add(15, 'minute'))
            .diff(startDateTime, 'minute');

        const blockHeight = ((durationMins / 15) * 50) - 28;

        const fullName = getFullName(match);

        return isStartSlot ? (

            <Box
                display='flex'
                flexDirection='row'
                justifyContent='flex-start'
                sx={{
                    position: 'absolute',
                    top: 3,
                    height: `${blockHeight}px`,
                    width: 'calc(100vw - 123px)',
                    backgroundColor: 'background.paper',
                    padding: '10px',
                    border: '1px solid ',
                    borderRadius: '4px',
                    zIndex: 10,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
                onClick={() => infoState ? null : openAppointmentDetailDialog(match)}
            >
                <InfoComponent infoContent='With click on existing appointment, you are able to manage them.'>
                    <Box display='flex' flexDirection='row'>
                        {/* <AssignmentIndIcon /> */}
                        <Typography variant="body1">
                            {fullName}
                        </Typography>
                    </Box>
                </InfoComponent>
                {renderCustomStatus(match.status)}
            </Box>
        ) : null;
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (serviceProviderAvailabilities.length) {
            timer = setTimeout(() => {
                const firstAvailableKey = Object.keys(timeSlotRefs.current).find(key => {
                    const [hour, min] = key.split('-').map(Number);
                    return checkHour(hour, min);
                });

                if (firstAvailableKey && timeSlotRefs.current[firstAvailableKey]) {
                    timeSlotRefs.current[firstAvailableKey]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 300);
        }
        return () => clearTimeout(timer);
    }, [selectedDate, serviceProviderAvailabilities, listType]);

    const hours = Array.from({ length: 24 }, (_, i) => ({
        id: i,
        title: dayjs().hour(i).minute(0).format('hh A')
    }));

    const minutes = [{ id: 0, title: '00' }, { id: 15, title: '15' }, { id: 30, title: '30' }, { id: 45, title: '45' }];

    const topServiceProviderMenu = 61;
    const topDateSelectable = 123;

    return (
        <Box>
            <ServiceProviderMenu top={topServiceProviderMenu} setListType={setListType} listType={listType} openNewAppointment={handleOpenEmptyAppointmentDialog} />
            <DateSelectable loading={loading || serviceProviderAvailabilitiesLoading} selectedDate={selectedDate} setSelectedDate={setSelectedDate} top={topDateSelectable} />
            {listType === 'timeSheet' && <Box ref={scrollRef} sx={{
                overflowY: 'auto', backgroundColor: 'background.paper',
                color: 'text.primary',
            }}>
                {hours.map((hour) =>
                    minutes.map((min) => {
                        const key = `${hour.id}-${min.id}`;
                        const available = checkHour(hour.id, min.id);
                        return (
                            <Box
                                key={key}
                                display="flex"
                                flexDirection="row"
                                sx={{
                                    borderBottom: '1px solid #ddd',
                                    alignItems: 'center',
                                    minHeight: '50px',
                                    backgroundColor: available ? 'background.default' : 'inherit',
                                }}
                            >
                                {min.id === 0 ? (
                                    <Box sx={{ width: '55px', textAlign: 'center', fontWeight: 'bold' }}>
                                        {hour.title}
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '55px' }} />
                                )}
                                <Box sx={{ width: '25px', textAlign: 'center' }}>{min.title}</Box>
                                <Box
                                    ref={(el: HTMLDivElement | null) => {
                                        timeSlotRefs.current[key] = el;
                                    }}
                                    sx={{
                                        flex: 1,
                                        height: '50px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        paddingLeft: '10px',
                                        borderLeft: '1px solid #eee',
                                        position: 'relative',
                                    }}
                                >
                                    {getAppointment(hour.id, min.id)}
                                </Box>
                            </Box>
                        );
                    })
                )}
            </Box>}
            {listType === 'gridTable' &&
                <Box>
                    <GridTable<ICustomAppointment, ICustomAppointment, ICustomAppointment>
                        loading={loading || serviceProviderAvailabilitiesLoading}
                        list={appointments.sort((x, y) => dayjs(x.startDateTime).isAfter(dayjs(y.startDateTime)) ? 1 : -1).map((item) => ({ ...item, id: item.appointmentId }))}
                        inputType={appointmentType}
                        onClickRow={openAppointmentDetailDialog}
                        onClickRowInfo='Select an existing appointment to manage it.'
                    />
                </Box>}
            {/* <ServiceProviderAvailabilityListDialog /> */}
            <AppointmentDialog state={appointmentDialogState} AllAppointment={appointments} closeAppointmentDialog={closeAppointmentDialog} />
            <AppointmentDetailDialog state={appointmentDetailDialogState} AllAppointment={appointments} closeAppointmentDialog={closeAppointmentDetailDialog} setAllAppointments={setAppointments} />
        </Box>
    );
};

export default AppointmentList;